/**
 * Field catalog, CSV templates, and mapping memory for the Import
 * Studio. The catalog is the human layer over importMapping.js — every field
 * here is one the backend actually consumes (see *_IMPORT_CONSUMED_FIELDS).
 */

import { FIELD_VARIATIONS } from '../importMapping';

/** Prettify a camelCase field name ("buyerAgentPhone" -> "Buyer Agent Phone"). */
export function fieldLabel(field) {
  if (field === 'loName') return 'LO Name (Full Name)';
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

/**
 * Origin's importable fields, organized for the Matching Board's right panel.
 * `required`: hard requirement. `requiredOneOf`: at least one of the group.
 */
export const CONTACT_FIELD_GROUPS = [
  {
    id: 'basic',
    title: 'Basic Info',
    fields: [
      { field: 'firstName', required: true, description: 'The contact\'s first name' },
      { field: 'lastName', required: true, description: 'The contact\'s last name' },
      { field: 'middleInitial', description: 'Middle initial' },
      { field: 'birthday', description: 'Birthday — "6/15/1985" and "1985-06-15" both work' },
    ],
  },
  {
    id: 'contact-details',
    title: 'Contact Details',
    fields: [
      { field: 'email', requiredOneOf: 'email-or-phone', description: 'Primary email — used to detect duplicates' },
      { field: 'phone', requiredOneOf: 'email-or-phone', description: 'Primary phone — used to detect duplicates' },
      { field: 'secondaryEmail', description: 'Alternate email address' },
      { field: 'secondaryPhone', description: 'Alternate phone number' },
      { field: 'preferredContactMethod', description: 'How they prefer to be reached (call, text, email)' },
    ],
  },
  {
    id: 'mailing',
    title: 'Mailing Address',
    fields: [
      { field: 'mailingStreet', description: 'Home / mailing street address' },
      { field: 'mailingCity', description: 'Mailing city' },
      { field: 'mailingState', description: 'Mailing state' },
      { field: 'mailingZip', description: 'Mailing ZIP code' },
    ],
  },
  {
    id: 'professional',
    title: 'Professional',
    fields: [
      { field: 'company', description: 'Company or brokerage name' },
      { field: 'jobTitle', description: 'Job title or role' },
      { field: 'licenseNumber', description: 'License / NMLS number' },
      { field: 'licenseState', description: 'State the license was issued in' },
      { field: 'website', description: 'Website URL' },
      { field: 'officeAddress', description: 'Office or business address' },
    ],
  },
  {
    id: 'classification',
    title: 'Classification',
    fields: [
      { field: 'contactTypes', description: 'Contact type(s) — must match your configured types (e.g. Borrower, Realtor)' },
      { field: 'tags', description: 'Comma-separated tags, added on top of any file-wide tags' },
      { field: 'source', description: 'Lead source (e.g. Zillow, Referral)' },
      { field: 'notes', description: 'Saved as a note on the contact\'s activity timeline' },
    ],
  },
];

export const DEAL_FIELD_GROUPS = [
  ...CONTACT_FIELD_GROUPS,
  {
    id: 'deal-pipeline',
    title: 'Deal — Pipeline',
    fields: [
      { field: 'stage', description: 'Pipeline stage — must match a configured stage (unknown values land in New Lead)' },
      { field: 'estimatedClosingDate', description: 'Estimated closing date' },
      { field: 'appDate', description: 'Loan application date' },
    ],
  },
  {
    id: 'deal-loan',
    title: 'Deal — Loan Details',
    fields: [
      { field: 'product', description: 'Loan product (e.g. Conventional, FHA, VA)' },
      { field: 'amount', description: 'Deal amount — "$250,000" and "250000" both work' },
      { field: 'loanAmount', description: 'Loan amount (if different from deal amount)' },
      { field: 'loanNumber', description: 'Loan number — used to detect duplicate deals' },
      { field: 'loanTerm', description: 'Term in months or years (numeric)' },
      { field: 'interestRate', description: 'Interest rate — "6.5%" works' },
      { field: 'lender', description: 'Lender name' },
      { field: 'loanPurpose', description: 'Purpose (purchase, refinance, cash-out...)' },
      { field: 'purchaseOrRefi', description: 'Purchase or refi transaction type' },
      { field: 'creditScore', description: 'Credit score (numeric)' },
      { field: 'downPayment', description: 'Down payment amount' },
      { field: 'occupancyType', description: 'Occupancy (primary residence, investment...)' },
    ],
  },
  {
    id: 'deal-property',
    title: 'Deal — Property',
    fields: [
      { field: 'propertyAddress', description: 'Street address of the property' },
      { field: 'propertyCity', description: 'Property city' },
      { field: 'propertyState', description: 'Property state' },
      { field: 'propertyZip', description: 'Property ZIP code' },
      { field: 'propertyType', description: 'Property type (SFR, condo...)' },
    ],
  },
  {
    id: 'deal-parties',
    title: 'Deal — Transaction Parties',
    fields: [
      { field: 'buyerAgentName', description: 'Buyer\'s agent name' },
      { field: 'buyerAgentPhone', description: 'Buyer\'s agent phone' },
      { field: 'buyerAgentEmail', description: 'Buyer\'s agent email' },
      { field: 'buyerAgentCompany', description: 'Buyer\'s agent brokerage' },
      { field: 'sellerAgentName', description: 'Listing agent name' },
      { field: 'sellerAgentPhone', description: 'Listing agent phone' },
      { field: 'sellerAgentEmail', description: 'Listing agent email' },
      { field: 'sellerAgentCompany', description: 'Listing agent brokerage' },
      { field: 'titleCompanyName', description: 'Title company name' },
      { field: 'titleCompanyPhone', description: 'Title company phone' },
      { field: 'titleCompanyEmail', description: 'Title company email' },
      { field: 'titleCompanyContact', description: 'Title company contact person' },
    ],
  },
];

/** RETR groups mirror the classic modal's mapping step. */
export const RETR_FIELD_GROUPS = [
  { id: 'retr-identity', title: 'RETR — Name & Identity', fields: ['loName', 'firstName', 'lastName', 'retrProfileId', 'retrNmlsId', 'retrLicensedSince', 'retrJobTitle'].map(f => ({ field: f, required: f === 'loName' })) },
  { id: 'retr-prod-state', title: 'RETR — Production (State)', fields: ['retrLoanCount', 'retrLoanVolume', 'retrConvCount', 'retrVaCount', 'retrFhaCount'].map(f => ({ field: f })) },
  { id: 'retr-prod-nation', title: 'RETR — Production (Nationwide)', fields: ['retrTotalLoanCount', 'retrTotalLoanVolume', 'retrAvgLoan', 'retrBuyerAgentsCount', 'retrYearsExperience', 'retrStateLicensesCount', 'retrFederalRegistrationsCount'].map(f => ({ field: f })) },
  { id: 'retr-profile', title: 'RETR — Profile & Employer', fields: ['retrLoType', 'retrLocation', 'retrProfileUrl', 'company', 'retrCompanyName', 'retrOfficeLocation', 'retrEmployerStartDate', 'retrBranchUrl'].map(f => ({ field: f })) },
  { id: 'retr-contact', title: 'RETR — Contact Info', fields: ['retrWorkEmail', 'retrPersonalEmail', 'retrOfficePhone', 'retrMobilePhone'].map(f => ({ field: f })) },
  { id: 'retr-social', title: 'RETR — Social & Web', fields: ['retrLinkedinUrl', 'retrFacebookUrl', 'retrTwitterUrl', 'retrInstagramUrl', 'retrZillowUrl', 'retrWebsiteUrl', 'retrExperienceUrl'].map(f => ({ field: f })) },
  { id: 'retr-search', title: 'RETR — Search Links', fields: ['retrGoogleSearchUrl', 'retrFacebookSearchUrl', 'retrLinkedinSearchUrl', 'retrZillowSearchUrl', 'retrNmlsLookupUrl'].map(f => ({ field: f })) },
  { id: 'retr-crm', title: 'CRM Contact Details (optional)', fields: ['email', 'phone'].map(f => ({ field: f })) },
];

export function getFieldGroups(importType, isRetrImport) {
  if (isRetrImport) return RETR_FIELD_GROUPS;
  return importType === 'deals' ? DEAL_FIELD_GROUPS : CONTACT_FIELD_GROUPS;
}

/** Flat list of catalog fields for an import type. */
export function getCatalogFields(importType, isRetrImport) {
  return getFieldGroups(importType, isRetrImport).flatMap(g => g.fields.map(f => f.field));
}

// ------------------------------------------------------------- CSV templates

const CONTACT_TEMPLATE_EXAMPLE = {
  firstName: 'Jordan', lastName: 'Rivera', middleInitial: 'A', birthday: '1985-06-15',
  email: 'jordan.rivera@example.com', phone: '(305) 555-0147',
  secondaryEmail: '', secondaryPhone: '', preferredContactMethod: 'text',
  mailingStreet: '742 Palm Court', mailingCity: 'Miami', mailingState: 'FL', mailingZip: '33101',
  company: 'Sunrise Realty', jobTitle: 'Realtor', licenseNumber: 'SL1234567',
  licenseState: 'FL', website: 'https://example.com', officeAddress: '100 Main St, Miami, FL 33101',
  contactTypes: 'Realtor', tags: 'VIP, Miami', source: 'Referral',
  notes: 'Met at the spring open house.',
};

const DEAL_TEMPLATE_EXAMPLE = {
  ...CONTACT_TEMPLATE_EXAMPLE,
  contactTypes: 'Borrower',
  stage: 'Processing', estimatedClosingDate: '2026-10-15', appDate: '2026-09-01',
  product: 'Conventional', amount: '$450,000', loanAmount: '$405,000',
  loanNumber: 'LN-2026-104', loanTerm: '360', interestRate: '6.25%',
  lender: 'UWM', loanPurpose: 'Purchase', purchaseOrRefi: 'purchase',
  creditScore: '742', downPayment: '$45,000', occupancyType: 'primary_residence',
  propertyAddress: '4821 Palm Ave', propertyCity: 'Miami', propertyState: 'FL',
  propertyZip: '33101', propertyType: 'single_family',
  buyerAgentName: 'Dana Smith', buyerAgentPhone: '(305) 555-0190',
  buyerAgentEmail: 'dana@sunriserealty.com', buyerAgentCompany: 'Sunrise Realty',
  sellerAgentName: '', sellerAgentPhone: '', sellerAgentEmail: '', sellerAgentCompany: '',
  titleCompanyName: 'Coastal Title', titleCompanyPhone: '', titleCompanyEmail: '', titleCompanyContact: '',
};

function csvEscape(value) {
  const str = String(value ?? '');
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/**
 * Build a downloadable CSV template. Headers are the canonical
 * FIELD_VARIATIONS spelling for each catalog field, so a filled-in template
 * auto-maps 100% on upload.
 */
export function buildTemplateCsv(importType) {
  const fields = getCatalogFields(importType, false);
  const example = importType === 'deals' ? DEAL_TEMPLATE_EXAMPLE : CONTACT_TEMPLATE_EXAMPLE;
  const headers = fields.map(f => (FIELD_VARIATIONS[f] ? FIELD_VARIATIONS[f][0] : f));
  const row = fields.map(f => csvEscape(example[f] ?? ''));
  return `${headers.map(csvEscape).join(',')}\n${row.join(',')}\n`;
}

export function downloadTemplateCsv(importType) {
  const csv = buildTemplateCsv(importType);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = importType === 'deals' ? 'origin-deals-template.csv' : 'origin-contacts-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ------------------------------------------------------------ mapping memory

const MAPPING_MEMORY_PREFIX = 'importStudioMapping:';

/** Stable signature for a header set — order-insensitive. */
export function headerSignature(headers, importType) {
  const normalized = [...(headers || [])].map(h => String(h).trim().toLowerCase()).sort().join('|');
  // djb2 — tiny, stable, good enough for a localStorage key
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) + hash + normalized.charCodeAt(i)) | 0;
  }
  return `${MAPPING_MEMORY_PREFIX}${importType}:${(hash >>> 0).toString(36)}`;
}

export function loadRememberedMapping(headers, importType) {
  try {
    const raw = localStorage.getItem(headerSignature(headers, importType));
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved !== 'object' || !saved.mapping) return null;
    // Only restore mappings whose headers still exist in this file
    const headerSet = new Set(headers);
    const mapping = Object.fromEntries(
      Object.entries(saved.mapping).filter(([, header]) => headerSet.has(header))
    );
    return Object.keys(mapping).length > 0 ? mapping : null;
  } catch {
    return null;
  }
}

export function saveRememberedMapping(headers, importType, mapping) {
  try {
    localStorage.setItem(
      headerSignature(headers, importType),
      JSON.stringify({ mapping, savedAt: new Date().toISOString() })
    );
  } catch { /* storage full/blocked — memory is a nicety */ }
}
